import axios from "axios";
import { APP_URL } from "../../services/serviceConstants";
import { CLIENT_INOCHI, encodeBase64 } from "./mmtFlow";

const api = axios.create({ baseURL: APP_URL });

api.interceptors.response.use(
  (response) => response,
  (err) => {
    const data = err?.response?.data;
    const apiMessage =
      data && typeof data === "object"
        ? data.message || data.error?.message
        : null;
    const refined = new Error(apiMessage || err.message);
    refined.response = err.response;
    throw refined;
  },
);

const authConfig = (session) => ({
  headers: {
    Authorization: `Bearer ${session.accessToken}`,
    "Accept-Language": session.locale || "en",
    Accept: "application/json",
  },
});

export const loginMmt = async (email, password) => {
  const response = await api.post("/v2/login", {
    authFlow: "USER_PASSWORD_AUTH",
    userType: 1,
    username: email,
    password: encodeBase64(password),
  });
  const data = response.data;
  if (!data?.status || !data?.result?.accessToken) {
    const message =
      data?.error?.message || data?.message || "Invalid email or password.";
    const error = new Error(message);
    error.isPasswordExpired = /expired/i.test(message);
    throw error;
  }
  return {
    data,
    session: {
      accessToken: data.result.accessToken,
      staffId: data.userData?.id || "",
      providerId: data.metaData?.providerId || "",
      adminId: data.metaData?.adminId || "",
      clientId: data.metaData?.clientId || "",
      role: data.userData?.role || "",
      locale: "en",
      client: CLIENT_INOCHI,
    },
  };
};

export const fetchLoggedUserInfo = async (session) => {
  const response = await api.get(
    `/users/${session.staffId}`,
    authConfig(session),
  );
  return response.data?.data || null;
};

export const fetchUserList = async ({
  session,
  page = 1,
  limit = 15,
  filter,
  sortField,
  sortOrder,
}) => {
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (filter) {
    query.set("filterField", "firstName,lastName,test_person_id");
    query.set("filter", filter);
  }
  if (sortField) query.set("sortField", sortField);
  if (sortOrder) query.set("sortOrder", sortOrder);
  const response = await api.get(
    `/v3/seniors?${query.toString()}`,
    authConfig(session),
  );
  return response.data?.data || [];
};

export const fetchTimeline = async (session) => {
  const providerId = encodeURIComponent(session.staffId);
  const response = await api.get(
    `/v5/notification/all?providerId=${providerId}`,
    authConfig(session),
  );
  return response.data?.data || [];
};

export const toPatientPayload = (step1, step2) => {
  const data = [
    { uniqueId: "first_name", value: (step1.firstname || "").trim() },
    { uniqueId: "last_name", value: (step1.lastname || "").trim() },
    {
      uniqueId: "user_id_from_client",
      value: (step1.testPersonId || "").trim(),
    },
    { uniqueId: "gender", value: step1.gender },
    {
      uniqueId: "birth_year",
      value: step1.birthYear ? Number(step1.birthYear) : null,
    },
    {
      uniqueId: "birth_month",
      value: step1.birthMonth ? Number(step1.birthMonth) : null,
    },
  ];
  if (step2) {
    if (step2.locationId != null) {
      data.push({
        uniqueId: "location_id",
        value: String(step2.locationId).trim(),
      });
    }
    data.push({
      uniqueId: "zip_code",
      value: step2.zipcode ? String(step2.zipcode).trim() : "",
    });
    data.push({ uniqueId: "fitness_level", value: step2.exercises || "" });
    data.push({
      uniqueId: "email",
      value: step2.email ? String(step2.email).trim() : "",
    });
    data.push({
      uniqueId: "mobile_phone_number",
      value: step2.phone ? String(step2.phone).trim() : "",
    });
    data.push({
      uniqueId: "height",
      value: step2.height ? Number(step2.height) : null,
    });
    data.push({
      uniqueId: "weight",
      value: step2.weight ? Number(step2.weight) : null,
    });
    if (step2.injuries && step2.injuries.length) {
      data.push({ uniqueId: "injury_or_surgery", value: step2.injuries });
    }
    if (step2.medications && step2.medications.length) {
      data.push({ uniqueId: "medications", value: step2.medications });
    }
    if (step2.painSymptoms && step2.painSymptoms.length) {
      data.push({ uniqueId: "pain_symptoms", value: step2.painSymptoms });
    }
  }
  return { data };
};

export const addPatient = async (session, step1, step2) => {
  const response = await api.post(
    "/seniors",
    toPatientPayload(step1, step2),
    authConfig(session),
  );
  return response.data;
};

export const getPatientDetails = async (session, seniorUuid) => {
  const response = await api.get(
    `/v4/seniors/${seniorUuid}`,
    authConfig(session),
  );
  return response.data?.data || null;
};

export const updatePatient = async (session, seniorUuid, step1, step2) => {
  const payload = toPatientPayload(step1, step2);
  const response = await api.put(
    `/v4/seniors/${seniorUuid}`,
    payload,
    authConfig(session),
  );
  return response.data;
};

export const signupMmt = async (email) => {
  const response = await api.get(
    `/users/validate?email=${encodeURIComponent(email)}`,
  );
  return response.data;
};

export const verifySignupOtp = async (email, otp) => {
  const response = await api.post("/users/validate", { email, otp });
  return response.data;
};

export const registerMmt = async ({ email, password, firstName, lastName }) => {
  const response = await api.post("/users/submit", {
    appType: "seniorNavigator",
    appInfo: {
      first_name: firstName,
      last_name: lastName,
      email,
      password: encodeBase64(password),
    },
  });
  return response.data;
};

export const requestPasswordReset = async (email) => {
  const response = await api.post("/forgetPassword", { username: email });
  if (!response.data?.status) {
    throw new Error(response.data?.message || "Failed to send reset code.");
  }
  return response.data;
};

export const resetPassword = async ({ email, otp, password }) => {
  const response = await api.put("/forgetPassword", {
    code: otp,
    username: email,
    password: encodeBase64(password),
  });
  if (!response.data?.status) {
    throw new Error(response.data?.message || "Failed to reset password.");
  }
  return response.data;
};

export const logoutMmt = async (session) => {
  try {
    await api.post(
      "/logout",
      { globalSignOut: false, token: session.accessToken },
      authConfig(session),
    );
  } catch {
    // The local session should still be cleared if the server logout fails.
  }
};
